import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Script from 'next/script';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import {
  faInstagram,
  faFacebook,
  faTiktok,
  faTelegram,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import Navbar from '../../Navbar';
import Api from '../../../helpers/api';
import l from '../../../helpers/lang';
import stripTags from '../../../helpers/strings';
import { SITE_INFO_CONTACTS } from '../../../helpers/constants';

export default class ContactsPage extends React.Component {
    static propTypes = {
      settings: PropTypes.shape({
        phone: PropTypes.string,
      }),
      contacts: PropTypes.shape({
        address: PropTypes.string,
        mapMarkers: PropTypes.arrayOf(PropTypes.shape({
          lat: PropTypes.string,
          lng: PropTypes.string,
        })),
        socials: PropTypes.shape({}),
      }),
    }

    constructor(props) {
      super(props);
      this.state = {
        contacts: props.contacts,
      };
    }

    async componentDidMount() {
      // Окно могут открыть в модалке, тогда не будет пропсов с контактами
      // Поэтому подгружаем эти данные тут
      if (!this.props?.contacts) {
        const api = new Api();
        const { contacts } = await api.getSiteInfo([SITE_INFO_CONTACTS]);
        this.setState({ contacts });
      }
    }

    initMap = async () => {
      const { mapMarkers: markers } = this.state.contacts;
      const { L } = window;

      if (markers.length === 0 || !L) {
        return;
      }

      const map = L.map('map');

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      const leafMakers = markers.map((marker) => L.marker([marker.lat, marker.lng]).addTo(map));

      if (markers.length > 1) {
        map.fitBounds((new L.featureGroup(leafMakers)).getBounds(), 11);
      } else {
        map.setView([markers[0].lat, markers[0].lng], 13);
      }
    }

    prepareLinksArray = () => {
      const { settings } = this.props;
      const { contacts: socials } = settings;
      const { contacts } = this.state;
      const allLinks = [];

      if (settings.phone) {
        allLinks.push({ icon: faPhone, url: `tel:${settings.phone}`, label: settings.phone });
      }

      const icons = {
        instagram: faInstagram,
        fb: faFacebook,
        tiktok: faTiktok,
        telegram: faTelegram,
        twitter: faXTwitter,
        youtube: faYoutube,
      };

      Object.entries(socials).forEach(([name, url]) => {
        if (!url || !icons[name]) return;

        const icon = icons[name];

        let label = url.replace('https://', '');
        label = `@${label.substr(label.indexOf('/') + 1)}`;

        // У ссылок иногда бывает слеш в конце
        if (label[label.length - 1] === '/') {
          label = label.substr(0, label.length - 1);
        }

        // У этих сетей в ссылке нет ника
        if (name === 'youtube') label = 'YouTube';

        allLinks.push({ icon, url, label });
      });

      if (contacts.email) {
        allLinks.push({ icon: faEnvelope, url: `mailto:${contacts.email}`, label: contacts.email });
      }

      return allLinks;
    };

    render() {
      const { navbar, settings, router } = this.props;
      const { contacts } = this.state;

      if (!contacts) {
        return null;
      }

      const { spots } = contacts;
      const { modal } = router.query;

      const preparedLink = this.prepareLinksArray();
      const hasContactsInfo = preparedLink.length
            || settings?.contacts?.address
            || stripTags(settings?.contacts?.additional)
            || spots.length > 1;

      return (
        <>
          <Head>
            <title>{l('delivery.navbar.contacts')}</title>
            <link
              rel="stylesheet"
              href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css"
              integrity="sha512-xodZBNTC5n17Xt2atTPuE1HxjVMSvLVW9ocqUKLsCC5CXdbqCmblAshOMAS6/keqq/sMZMZ19scR4PsZChSR7A=="
              crossOrigin=""
            />
          </Head>

          <Script
            src="https://unpkg.com/leaflet@1.7.1/dist/leaflet.js"
            integrity="sha512-XQoYMqMTK8LvdxXYG3nZ448hOEQiglfqkJs1NOQV44cWnUrBc8PkAOcXy20w0vlaXaVUearIOBhiXZ5V3ynxwA=="
            crossOrigin
            onReady={this.initMap}
          />

          {/* Если это открытая модалка то не показываем navbar */}
          {!modal && (
            <Navbar
              menu={navbar}
              logo={settings.logo}
              title={settings.shopName}
            />
          )}

          {Boolean(contacts.mapMarkers.length) && (
            <div id="map" className="mb-3" style={{ width: '100%', height: '250px', minHeight: '250px' }} />
          )}

          <div className="container p-5">
            <div className="row">
              <div className={`${spots.length > 1 ? 'offset-md-2' : 'offset-md-4'} col-md-5`}>
                <h1 className="fs-2 fw-normal mb-5">
                  {l('delivery.navbar.contacts')}
                </h1>
                {preparedLink.map(({ url, label, icon }) => (
                  <Link href={url} target="_blank" className="mb-3 d-block" key={url}>
                    <div className="d-inline-block align-middle me-2">
                      <FontAwesomeIcon width={32} height={32} icon={icon} />
                    </div>
                    <div className="d-inline-block align-middle text-body">
                      {label}
                    </div>
                  </Link>
                ))}

                { hasContactsInfo && (
                <>
                  <div
                    className="mb-4"
                    dangerouslySetInnerHTML={{
                      __html: (settings.contacts.address || (spots.length > 0 && spots[0].address)),
                    }}
                  />
                  <div dangerouslySetInnerHTML={{ __html: settings.contacts.additional }} />
                </>
                )}

                { !hasContactsInfo && (
                <div>{l('delivery.navbar.noContactsInformation')}</div>
                )}
              </div>

              {spots.length > 1 && (
                <div className="col-md-5">
                  <h2 className="fs-2 fw-normal mb-5">{l('delivery.contacts.spots')}</h2>
                  {spots.map((spot) => (
                    <div className="rounded shadow-sm py-3 px-4 mb-3" key={spot.posId}>
                      <h5 className="fs-4 fw-bold">{spot.name}</h5>
                      {Boolean(spot.address) && (
                      <div>
                        <span className="align-middle ms-n2">
                              <FontAwesomeIcon width={32} height={32} icon={faMapMarkerAlt} />
                            </span>
                        <span className="align-middle">{spot.address}</span>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      );
    }
}
